import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", company: "", email: "", phone: "", subject: "Audit & CEE", message: "" };

const inputCls =
  "w-full bg-navy-800/70 border border-gold/20 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/50 transition-colors";

const infos = [
  { icon: Mail, label: "E-mail", value: "contact@levi-one.fr", href: "mailto:contact@levi-one.fr", testid: "contact-info-email" },
  { icon: Phone, label: "Téléphone", value: "07 66 65 37 36", href: "tel:+33766653736", testid: "contact-info-phone" },
  { icon: MapPin, label: "Siège", value: "45 Allée Arthur Rimbaud, 95350 Saint-Brice-sous-Forêt", testid: "contact-info-address" },
  { icon: Clock, label: "Horaires", value: "Lun – Ven, 9 h à 18 h", testid: "contact-info-hours" },
];

const faqs = [
  {
    q: "Qu'est-ce que les CEE et comment en bénéficier ?",
    a: "Les Certificats d'Économies d'Énergie (CEE) obligent les fournisseurs d'énergie à financer des travaux d'efficacité énergétique. Pour votre entreprise, c'est une prime qui peut couvrir une part significative de vos travaux. Nous identifions vos opérations éligibles, montons le dossier et valorisons vos certificats au meilleur prix.",
  },
  {
    q: "Mon entreprise est-elle concernée par le Décret Tertiaire 2030 ?",
    a: "Le Dispositif Éco-Énergie Tertiaire s'applique aux bâtiments (ou parties de bâtiments) à usage tertiaire de plus de 1 000 m² : bureaux, commerces, logistique, santé, enseignement... Il impose une réduction des consommations de -40 % en 2030, -50 % en 2040 et -60 % en 2050, avec déclaration annuelle sur la plateforme OPERAT.",
  },
  {
    q: "Quels sont les risques en cas de non-conformité au Décret Tertiaire ?",
    a: "En l'absence de déclaration ou d'atteinte des objectifs, vous encourez une publication de votre non-conformité (« name and shame ») puis des sanctions financières pouvant atteindre 7 500 € par personne morale, renouvelables. Un accompagnement structuré permet de sécuriser votre trajectoire en amont.",
  },
];

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Votre demande a bien été envoyée. Un conseiller vous recontacte sous 24 h ouvrées.");
      setForm(empty);
    } catch {
      toast.error("Une erreur est survenue. Merci de réessayer ou de nous écrire directement à contact@levi-one.fr.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative overflow-hidden border-b border-gold/10">
        <div className="absolute inset-0 hero-glow" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-24">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-gold mb-4">Contact</p>
            <h1 className="text-4xl sm:text-5xl font-serif font-bold tracking-tight text-white leading-[1.15]">
              Parlons de votre <span className="gold-text">projet énergétique</span>
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-slate-300 font-light">
              Premier échange sans engagement : nous évaluons votre situation, vos obligations
              et votre potentiel d'économies.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20 grid lg:grid-cols-[1.2fr_1fr] gap-12">
        <Reveal>
          <form onSubmit={submit} className="bg-navy-800/50 border border-gold/15 rounded-3xl p-8 sm:p-10 space-y-5" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="contact-name" className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Nom complet *</label>
                <input id="contact-name" data-testid="contact-form-name" required minLength={2} value={form.name} onChange={update("name")} placeholder="Jean Dupont" className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-company" className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Société</label>
                <input id="contact-company" data-testid="contact-form-company" value={form.company} onChange={update("company")} placeholder="Votre entreprise" className={inputCls} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="contact-email" className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">E-mail professionnel *</label>
                <input id="contact-email" type="email" data-testid="contact-form-email" required value={form.email} onChange={update("email")} placeholder="jean@entreprise.fr" className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-phone" className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Téléphone</label>
                <input id="contact-phone" type="tel" data-testid="contact-form-phone" value={form.phone} onChange={update("phone")} placeholder="06 12 34 56 78" className={inputCls} />
              </div>
            </div>
            <div>
              <label htmlFor="contact-subject" className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Sujet de votre demande</label>
              <select id="contact-subject" data-testid="contact-form-subject" value={form.subject} onChange={update("subject")} className={inputCls}>
                <option>Audit & CEE</option>
                <option>Rénovation énergétique</option>
                <option>Décret Tertiaire 2030</option>
                <option>Devenir partenaire</option>
                <option>Autre demande</option>
              </select>
            </div>
            <div>
              <label htmlFor="contact-message" className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Votre message *</label>
              <textarea id="contact-message" data-testid="contact-form-message" required minLength={10} rows={5} value={form.message} onChange={update("message")} placeholder="Décrivez votre bâtiment, votre activité et votre besoin..." className={`${inputCls} resize-y`} />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-form-submit-btn"
              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto bg-gradient-to-r from-gold to-gold-bright hover:from-gold-bright hover:to-gold disabled:opacity-60 text-navy-950 font-semibold px-8 py-3.5 rounded-full shadow-lg shadow-gold/25 transition-all duration-300 hover:-translate-y-0.5"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              {loading ? "Envoi en cours..." : "Envoyer ma demande"}
            </button>
          </form>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="space-y-6">
            <div className="bg-navy-800/50 border border-gold/15 rounded-3xl p-8">
              <h2 className="text-lg font-medium text-white mb-6">Nos coordonnées</h2>
              <ul className="space-y-5">
                {infos.map((i) => (
                  <li key={i.label} className="flex items-center gap-4" data-testid={i.testid}>
                    <span className="w-10 h-10 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center shrink-0">
                      <i.icon size={17} className="text-gold-bright" />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{i.label}</p>
                      {i.href ? (
                        <a href={i.href} data-testid={`${i.testid}-link`} className="text-sm text-slate-200 hover:text-gold-bright transition-colors">
                          {i.value}
                        </a>
                      ) : (
                        <p className="text-sm text-slate-200">{i.value}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-gold/15 to-transparent border border-gold/25 rounded-3xl p-8">
              <h2 className="text-lg font-medium text-white mb-3">Réponse sous 24 h ouvrées</h2>
              <p className="text-sm leading-relaxed text-slate-400">
                Chaque demande est étudiée par un conseiller Levi One. Premier échange et pré-diagnostic
                sans engagement.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="max-w-4xl mx-auto px-6 sm:px-10 pb-24" data-testid="faq-section">
        <Reveal className="text-center mb-10">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold mb-3">FAQ</p>
          <h2 className="text-2xl sm:text-3xl font-serif font-semibold text-white">Questions fréquentes</h2>
        </Reveal>
        <Reveal>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`faq-${i}`}
                data-testid={`faq-item-${i}`}
                className="bg-navy-800/50 border border-gold/15 rounded-xl px-6 data-[state=open]:border-gold/40 transition-colors"
              >
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-sm sm:text-base font-medium text-white hover:text-gold-bright hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-400 pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>
    </div>
  );
}
